import React from 'react';
import { Chip, MenuItem, TextField, Typography } from '@mui/material';
import { GridColDef, GridRenderEditCellParams, GridValidRowModel } from '@mui/x-data-grid';
import { FDataGrid, FGridMoneyCell } from '../grid';
import { BudgetArbitrationRow } from './budgetconsolidation.models';

type Props = {
  rows: BudgetArbitrationRow[];
  loading?: boolean;
  editable?: boolean;
  onRowsChange: (rows: BudgetArbitrationRow[]) => void;
  onSave: (rows: BudgetArbitrationRow[]) => Promise<void>;
};

const decisions = ['PENDING', 'APPROVE', 'REDUCE', 'INCREASE', 'REJECT', 'DEFER'];

export default class BudgetArbitrationGrid extends React.PureComponent<Props> {
  private validate = (row: BudgetArbitrationRow) => {
    if (!Number.isFinite(Number(row.arbitratedamount)) || Number(row.arbitratedamount) < 0)
      return 'Arbitrated amount must be zero or greater.';
    if (row.decisioncode !== 'PENDING' && !String(row.reason || '').trim())
      return 'A decision reason is required.';
    return '';
  };

  private processRowUpdate = (next: GridValidRowModel, previous: GridValidRowModel) => {
    const requestedamount = Number(next.requestedamount || 0);
    const arbitratedamount = Number(next.arbitratedamount || 0);
    const updated: BudgetArbitrationRow = {
      ...(previous as BudgetArbitrationRow),
      ...(next as BudgetArbitrationRow),
      requestedamount,
      arbitratedamount,
      varianceamount: arbitratedamount - requestedamount,
      variancepercent: requestedamount === 0 ? 0 : (arbitratedamount - requestedamount) / requestedamount * 100,
      _state: 'dirty',
      __dirty: true,
      __error: '',
    };
    updated.__error = this.validate(updated);
    this.props.onRowsChange(this.props.rows.map(row => row.id === updated.id ? updated : row));
    return updated;
  };

  private renderDecisionEditor = (params: GridRenderEditCellParams) => (
    <TextField
      select
      fullWidth
      size="small"
      value={params.value || 'PENDING'}
      onChange={event => params.api.setEditCellValue({ id: params.id, field: params.field, value: event.target.value })}
    >
      {decisions.map(code => <MenuItem key={code} value={code}>{code}</MenuItem>)}
    </TextField>
  );

  private columns: GridColDef[] = [
    { field: 'sourceclasscode', headerName: 'Budget', width: 120, cellClassName: 'master-cell' },
    { field: 'sourcecode', headerName: 'Reference', width: 150, cellClassName: 'master-cell' },
    { field: 'sourcename', headerName: 'Budget Line / Activity', minWidth: 260, flex: 1, cellClassName: 'master-cell' },
    { field: 'costcentercode', headerName: 'Cost Center', width: 130, cellClassName: 'master-cell' },
    { field: 'accountcode', headerName: 'Account', width: 130, cellClassName: 'master-cell' },
    { field: 'requestedamount', headerName: 'Requested', width: 150, renderCell: FGridMoneyCell, cellClassName: 'source-cell' },
    { field: 'recommendedamount', headerName: 'Recommended', width: 150, renderCell: FGridMoneyCell, cellClassName: 'computed-cell' },
    { field: 'arbitratedamount', headerName: 'Arbitrated', width: 150, editable: true, renderCell: FGridMoneyCell, cellClassName: 'input-cell' },
    { field: 'varianceamount', headerName: 'Variance', width: 145, renderCell: FGridMoneyCell, cellClassName: 'computed-cell' },
    { field: 'variancepercent', headerName: 'Variance %', width: 120, type: 'number', cellClassName: 'computed-cell' },
    {
      field: 'decisioncode', headerName: 'Decision', width: 130, editable: true,
      renderEditCell: this.renderDecisionEditor,
      renderCell: params => <Chip size="small" label={params.value || 'PENDING'} />,
      cellClassName: 'input-cell',
    },
    { field: 'reason', headerName: 'Decision Reason', minWidth: 260, flex: 1, editable: true, cellClassName: 'input-cell' },
    { field: 'remarks', headerName: 'Remarks', minWidth: 220, flex: 1, editable: true, cellClassName: 'input-cell' },
    {
      field: '__error', headerName: 'Validation', minWidth: 240,
      renderCell: params => params.value ? <Typography variant="caption" color="error.main">{params.value}</Typography> : null,
    },
  ];

  render() {
    const dirty = this.props.rows.some(row => row.__dirty);
    const invalid = this.props.rows.some(row => Boolean(row.__error || this.validate(row)));

    return (
      <FDataGrid
        title="Budget Arbitration"
        subtitle="Record governance decisions separately from source budgets. Amber cells are arbitration decisions; source and computed values remain read-only."
        rows={this.props.rows || []}
        columns={this.columns}
        loading={this.props.loading}
        height={520}
        getRowId={(row: BudgetArbitrationRow) => row.id}
        processRowUpdate={this.processRowUpdate}
        onProcessRowUpdateError={console.error}
        isCellEditable={params => !!this.props.editable && ['arbitratedamount', 'decisioncode', 'reason', 'remarks'].includes(params.field)}
        saveAll={!this.props.editable || !dirty || invalid ? undefined : () => this.props.onSave(this.props.rows)}
        getRowClassName={params => params.row.__error ? 'arbitration-row-error' : params.row.__dirty ? 'arbitration-row-dirty' : ''}
        sx={{
          '& .master-cell': { bgcolor: 'info.lighter' },
          '& .source-cell': { bgcolor: 'action.hover' },
          '& .computed-cell': { bgcolor: 'success.lighter', fontWeight: 700 },
          '& .input-cell': { bgcolor: 'warning.lighter', fontWeight: 600 },
          '& .arbitration-row-error': { bgcolor: 'error.lighter' },
          '& .arbitration-row-dirty': { boxShadow: 'inset 3px 0 0 0', boxShadowColor: 'warning.main' },
        }}
      />
    );
  }
}
